/**
 * One warm line per setup step instead of a numbered step bar (Sam, Oct 11 2026).
 * Pure helpers so every setup surface says the same thing.
 * Talker lines never mention earning; no promo wording anywhere.
 */

export const CHEER_TONE_TALK = 'talk';
export const CHEER_TONE_LISTEN = 'listen';

// Step ids from both the server status and the older Become a Listener / signup flows.
const PAYOUT_STEP_IDS = new Set(['payouts', 'payout']);
const LISTENER_STEP_IDS = new Set([
  'payouts',
  'payout',
  'listenProfile',
  'listening',
  'availability',
  'profile',
]);

export const CHEER_LINES = {
  first: 'Welcome in. Let’s get you set up.',
  next: 'Nice work! On to the next one.',
  quick: 'Great progress! This one only takes a couple of minutes.',
  halfway: 'You’re more than halfway there.',
  almost: 'So close now. Keep going!',
  earning: 'Almost ready to start earning.',
  last: 'Last one! You’ve got this.',
};

/**
 * @param {Object} params
 * @param {Array<string>} params.steps ordered step ids
 * @param {string|null} params.current current step id
 * @param {string|null} [params.role] 'talk' | 'listen' | 'both'
 * @returns {{ key: string, text: string, tone: string }|null}
 */
export const onboardingCheer = ({ steps = [], current = null, role = null } = {}) => {
  const list = Array.isArray(steps) ? steps : [];
  const index = list.indexOf(current);
  if (!current || index < 0 || list.length === 0) {
    return null;
  }
  const listenerStep = LISTENER_STEP_IDS.has(current);
  const tone =
    role === 'listen' || (role !== 'talk' && listenerStep) ? CHEER_TONE_LISTEN : CHEER_TONE_TALK;
  const isListenerish = role === 'listen' || role === 'both' || listenerStep;
  const total = list.length;
  let key;
  if (total > 1 && index === total - 1) {
    key = 'last';
  } else if (PAYOUT_STEP_IDS.has(current) && isListenerish) {
    key = 'earning';
  } else if (current === 'verify' && index === 1) {
    key = 'next';
  } else if (current === 'identity' || current === 'verify') {
    key = 'quick';
  } else if (index === 0) {
    key = 'first';
  } else if (index / (total - 1) >= 0.75) {
    key = 'almost';
  } else if (index / (total - 1) >= 0.5) {
    key = 'halfway';
  } else {
    key = 'next';
  }
  return { key, text: CHEER_LINES[key], tone };
};

/** Guess the role from a step list (older flows that don't pass one). */
export const roleFromStepIds = (ids = []) => {
  const hasListen = ids.some(id => LISTENER_STEP_IDS.has(id));
  const hasTalk = ids.some(id => id === 'card' || id === 'talkProfile' || id === 'talking');
  return hasListen && hasTalk ? 'both' : hasListen ? 'listen' : 'talk';
};

export const CELEBRATION_COPY = {
  talk: {
    title: 'You’re all set!',
    body: 'Your account is ready. Find a listener who feels right and start a conversation.',
    cta: 'Browse listeners',
    ctaRoute: 'SearchPage',
  },
  listen: {
    title: 'You’re all set!',
    body: 'Your Listening profile is ready. Set your work hours so talkers can reach you.',
    cta: 'Go to Available now',
    ctaRoute: 'DashboardPage',
    secondary: 'Browse listeners',
    secondaryRoute: 'SearchPage',
  },
};

/** @param {string|null} role */
export const celebrationFor = role =>
  role === 'listen' || role === 'both' ? CELEBRATION_COPY.listen : CELEBRATION_COPY.talk;

const SEEN_PREFIX = 'sl-onboard-celebrated:';
const ACTIVE_PREFIX = 'sl-onboard-active:';

const store = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (e) {
    return null;
  }
};

/** Remember that this person was mid-setup on this device. */
export const markOnboardingActive = userId => {
  const s = store();
  if (s && userId) {
    try {
      s.setItem(ACTIVE_PREFIX + userId, '1');
    } catch (e) {
      // ignore
    }
  }
};

/** Should we pop the celebration now (finished setup on this device, not yet shown)? */
export const shouldCelebrate = userId => {
  const s = store();
  if (!s || !userId) {
    return false;
  }
  try {
    return s.getItem(ACTIVE_PREFIX + userId) === '1' && s.getItem(SEEN_PREFIX + userId) !== '1';
  } catch (e) {
    return false;
  }
};

export const markCelebrated = userId => {
  const s = store();
  if (s && userId) {
    try {
      s.setItem(SEEN_PREFIX + userId, '1');
      s.removeItem(ACTIVE_PREFIX + userId);
    } catch (e) {
      // ignore
    }
  }
};
